import { ApiFetchError, apiFetch } from "../utils/apiFetch";

/**
 * Der Austausch („Rücksprache") an einem Strang eines Vorgangs (T-302).
 *
 * Ticket (Sascha Hahn, 13.10.2026): „Es fehlt eine Art ‚Rücksprachebutton' in
 * den Vorgängen, z. B. R/DC." Ein Strang ist der Vorgang selbst (ohne
 * To-dos, `todoId` null) oder eines seiner To-dos; je Strang ist höchstens
 * EIN Austausch offen.
 */
/**
 * Mit wem (PO 01.11.2026): eine Kollegin/ein Kollege aus KMVS (`userId`) oder
 * jemand Externes, so wie eingetippt (`name`).
 */
export type AustauschTeilnehmer = { userId: string } | { name: string };

export interface AustauschDto {
    id: number;
    vorgangId: number;
    /** null = am Vorgang selbst. */
    todoId: number | null;
    /** Mit wem — mindestens einer. */
    teilnehmer: AustauschTeilnehmer[];
    /** Alt (bis 01.11.2026): die eine Person; neue Zeilen tragen die erste Kollegin oder null. */
    mitUserId: string | null;
    /** Alt: geplanter Zeitpunkt; neue Zeilen haben keinen. */
    zeitpunkt: string | null;
    /** Alt: Notiz; wird nicht mehr bearbeitet. */
    notiz: string | null;
    /** null = offen. */
    erledigtAm: string | null;
    /** null bei einem Austausch, den ein Umbau (Zusammenführen) geschlossen hat. */
    erledigtVon: string | null;
    angelegtVon: string;
    createdAt: string;
    updatedAt: string;
}

/**
 * Was die Vorgangsliste je Vorgang mitbekommt: alle offenen Austausche des
 * Vorgangs und seiner To-dos zusammengefasst.
 */
export interface OffenerAustauschDto {
    id: number;
    todoId: number | null;
    /** Wie viele Stränge einen offenen Austausch haben. */
    anzahl: number;
    /** Alle Teilnehmer, ohne Doppelte. */
    teilnehmer: AustauschTeilnehmer[];
}

export type AustauschAnlage = {
    todoId?: number | null;
    teilnehmer: AustauschTeilnehmer[];
};

export type AustauschAenderung = {
    teilnehmer?: AustauschTeilnehmer[];
    /** Nur `true` — wieder öffnen gibt es nicht, dann neu eintragen. */
    erledigt?: true;
};

const alsJson = (method: "POST" | "PATCH", body: unknown): RequestInit => ({
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
});

export const vorgangAustauschService = {
    liste: async (vorgangId: number): Promise<AustauschDto[]> =>
        (await apiFetch<AustauschDto[]>(`vorgang/${vorgangId}/austausch`)).data,
    anlegen: async (vorgangId: number, eingabe: AustauschAnlage): Promise<AustauschDto> =>
        (await apiFetch<AustauschDto>(`vorgang/${vorgangId}/austausch`, alsJson("POST", eingabe))).data,
    aendern: async (id: number, aenderung: AustauschAenderung): Promise<AustauschDto> =>
        (await apiFetch<AustauschDto>(`vorgang/austausch/${id}`, alsJson("PATCH", aenderung))).data,
};

const ALLGEMEIN = "Der Austausch konnte nicht gespeichert werden. Bitte später erneut versuchen.";

/** Statuscodes, bei denen die `message` aus unserem Controller stammt und deutsch ist. */
const EIGENE_TEXTE = new Set([400, 404, 409]);

/**
 * Text für den Nutzer zu einem fehlgeschlagenen Aufruf — dasselbe Muster wie
 * `remarkableFehlertext`: 401/403 bringen englische Technik-Meldungen des
 * Guards mit und bekommen feste deutsche Sätze; nur die eigenen, deutschen
 * Meldungen des Controllers werden durchgereicht.
 */
export function austauschFehlertext(e: unknown): string {
    if (!(e instanceof ApiFetchError)) return ALLGEMEIN;
    if (e.status === 401) return "Die Anmeldung ist abgelaufen. Bitte neu anmelden.";
    if (e.status === 403) return "Nur wer den Vorgang bearbeiten darf, kann einen Austausch eintragen oder ändern.";
    if (EIGENE_TEXTE.has(e.status)) {
        try {
            const m = (JSON.parse(e.message.replace(/^HTTP \d+: /, "")) as { message?: unknown } | null)?.message;
            if (typeof m === "string" && m.trim()) return m;
        } catch {
            /* keine JSON-Antwort */
        }
    }
    return ALLGEMEIN;
}
